// src/ChangePassword.js
import React, { useState } from "react";
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Alert, 
  Paper, 
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";

const ChangePassword = ({ open, onClose }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");
    
    // Validate form fields
    if (!currentPassword.trim()) {
      setErrorMsg("Current password is required.");
      return;
    }
    
    if (!newPassword.trim()) {
      setErrorMsg("New password is required.");
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setErrorMsg("New passwords do not match.");
      return;
    }

    if (currentPassword === newPassword) {
      setErrorMsg("New password must be different from current password.");
      return;
    }

    setLoading(true);

    // Get the token from localStorage
    const token = localStorage.getItem("token");

    // Send password change request to server
    fetch(`${process.env.REACT_APP_API_URL || ''}/api/auth/change-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify({
        currentPassword,
        newPassword
      }),
    })
      .then((res) => {
        if (!res.ok) {
          return res.json().then((data) => {
            throw new Error(data.error || "Password change failed");
          });
        }
        return res.json();
      })
      .then((data) => {
        setSuccessMsg("Password changed successfully!");
        // Clear form
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        
        // Close the dialog after a short delay
        setTimeout(() => {
          onClose();
        }, 2000);
      })
      .catch((error) => {
        setErrorMsg(error.message || "Password change failed. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleClose = () => {
    // Reset form state
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setErrorMsg("");
    setSuccessMsg("");
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Change Password</DialogTitle>
      <DialogContent>
        {errorMsg && <Alert severity="error" style={{ marginBottom: "10px" }}>{errorMsg}</Alert>}
        {successMsg && <Alert severity="success" style={{ marginBottom: "10px" }}>{successMsg}</Alert>}
        <form onSubmit={handleSubmit}>
          <TextField
            label="Current Password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth
            margin="normal"
            required
            error={newPassword !== confirmPassword && confirmPassword !== ""}
            helperText={
              newPassword !== confirmPassword && confirmPassword !== ""
                ? "Passwords do not match"
                : ""
            }
          />
          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? "Changing..." : "Change Password"}
            </Button>
          </Box>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePassword;